import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { pantryColors, pantryRadii } from '@pantry/ui'
import { PlusIcon, specAddButtonStyle } from './CustomSpecificationsInput'
import { MAX_CUSTOM_SPECS, specColors } from '../../constants'
import { CANNABINOID_NAMES } from '../../utils/variationSpecs'
import type { CannabinoidName, HiddenSpecField } from '../../../../types/product'

// "+ Add field" menu (ACD-15223 C6): lists hidden cannabinoids and hidden chip
// fields so they can be shown again, and always ends with "+ Custom
// specification". Rendered in a portal with fixed positioning so the
// accordion card's overflow can't clip it.

export type SpecListField = 'terpenes' | 'effects' | 'flavours'

export const SPEC_LIST_FIELD_LABELS: Record<SpecListField, string> = {
  terpenes: 'Terpenes',
  effects: 'Effects',
  flavours: 'Flavours',
}

const SPEC_LIST_FIELDS: SpecListField[] = ['terpenes', 'effects', 'flavours']

interface AddSpecFieldMenuProps {
  hidden: readonly HiddenSpecField[]
  onShow: (field: HiddenSpecField) => void
  onAddCustom: () => void
  label?: string
  disabled?: boolean
  // Disables the "Custom specification" entry (e.g. at the row limit).
  customDisabled?: boolean
}

interface MenuCoords {
  top: number
  left: number
}

const VIEWPORT_GAP = 8

const groupLabelStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '.5px',
  color: pantryColors.gray[400],
  padding: '6px 10px 2px',
}

const separatorStyle: React.CSSProperties = { height: 1, background: pantryColors.gray[100], margin: '4px 0' }

const itemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  padding: '8px 10px',
  border: 'none',
  background: 'none',
  borderRadius: pantryRadii.sm,
  fontSize: 13,
  fontFamily: 'inherit',
  color: pantryColors.gray[800],
  cursor: 'pointer',
  textAlign: 'left',
}

const MenuItem: React.FC<{ onSelect: () => void; children: React.ReactNode; disabled?: boolean; title?: string }> = ({
  onSelect,
  children,
  disabled,
  title,
}) => (
  <button
    type="button"
    role="menuitem"
    onClick={onSelect}
    disabled={disabled}
    title={title}
    style={disabled ? { ...itemStyle, color: pantryColors.gray[400], cursor: 'default' } : itemStyle}
    onMouseEnter={(e) => {
      if (!disabled) e.currentTarget.style.background = pantryColors.gray[100]
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = 'none'
    }}
  >
    {children}
  </button>
)

// Below the anchor, or above it when there isn't room below (and more room above).
export function placeMenu(anchor: DOMRect, menuHeight: number, menuWidth: number, viewport: { width: number; height: number }): MenuCoords {
  const below = viewport.height - anchor.bottom - VIEWPORT_GAP
  const top = below >= menuHeight || below >= anchor.top ? anchor.bottom + 4 : anchor.top - menuHeight - 4
  return {
    top: Math.max(VIEWPORT_GAP, top),
    left: Math.max(VIEWPORT_GAP, Math.min(anchor.left, viewport.width - menuWidth - VIEWPORT_GAP)),
  }
}

export const AddSpecFieldMenu: React.FC<AddSpecFieldMenuProps> = ({
  hidden,
  onShow,
  onAddCustom,
  label = 'Add field',
  disabled = false,
  customDisabled = false,
}) => {
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState<MenuCoords | null>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const hiddenCannabinoids = CANNABINOID_NAMES.filter((name) => hidden.includes(name))
  const hiddenFields = SPEC_LIST_FIELDS.filter((field) => hidden.includes(field))

  const close = useCallback(() => {
    setOpen(false)
    setCoords(null)
  }, [])

  const reposition = useCallback(() => {
    const anchor = buttonRef.current
    const menu = menuRef.current
    if (!anchor || !menu) return
    const rect = anchor.getBoundingClientRect()
    setCoords(
      placeMenu(rect, menu.offsetHeight, menu.offsetWidth, { width: window.innerWidth, height: window.innerHeight }),
    )
  }, [])

  useLayoutEffect(() => {
    if (open) reposition()
  }, [open, reposition])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (menuRef.current?.contains(target) || buttonRef.current?.contains(target)) return
      close()
    }
    // Follow the anchor while the page scrolls; close once it leaves the viewport.
    const onScroll = (e: Event) => {
      if (e.target instanceof Node && menuRef.current?.contains(e.target)) return
      const rect = buttonRef.current?.getBoundingClientRect()
      if (!rect || !rect.width || rect.bottom < 0 || rect.top > window.innerHeight) close()
      else reposition()
    }
    // Capture phase + preventDefault: Esc closes only the menu, not an
    // enclosing dialog that listens for Escape on the document.
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      e.stopPropagation()
      close()
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', reposition)
    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', reposition)
      document.removeEventListener('keydown', onKeyDown, true)
    }
  }, [open, close, reposition])

  const select = (action: () => void) => {
    close()
    action()
  }

  const groups: React.ReactNode[] = []
  if (hiddenCannabinoids.length > 0) {
    groups.push(
      <div key="cannabinoids" role="group" aria-label="Cannabinoids">
        <div style={groupLabelStyle}>Cannabinoids</div>
        {hiddenCannabinoids.map((name: CannabinoidName) => (
          <MenuItem key={name} onSelect={() => select(() => onShow(name))}>
            <span
              aria-hidden
              style={{ width: 7, height: 7, borderRadius: '50%', background: specColors.cannabinoid[name], flexShrink: 0 }}
            />
            {name}
          </MenuItem>
        ))}
      </div>,
    )
  }
  if (hiddenFields.length > 0) {
    groups.push(
      <div key="fields" role="group" aria-label="Fields">
        <div style={groupLabelStyle}>Fields</div>
        {hiddenFields.map((field) => (
          <MenuItem key={field} onSelect={() => select(() => onShow(field))}>
            {SPEC_LIST_FIELD_LABELS[field]}
          </MenuItem>
        ))}
      </div>,
    )
  }

  const menu = open
    ? ReactDOM.createPortal(
        <div
          ref={menuRef}
          role="menu"
          aria-label={label}
          style={{
            position: 'fixed',
            top: coords?.top ?? 0,
            left: coords?.left ?? 0,
            // Measured first, then placed — avoids a flash at 0,0.
            visibility: coords ? 'visible' : 'hidden',
            zIndex: 1300,
            maxHeight: `min(360px, calc(100vh - ${VIEWPORT_GAP * 2}px))`,
            overflow: 'auto',
            minWidth: 220,
            boxSizing: 'border-box',
            background: pantryColors.white,
            border: `1px solid ${pantryColors.gray[200]}`,
            borderRadius: pantryRadii.md,
            boxShadow: '0 10px 25px rgba(0,0,0,.12)',
            padding: 4,
          }}
        >
          {groups.length > 0 ? (
            groups.map((group, i) => (
              <React.Fragment key={i === 0 ? 'first' : 'second'}>
                {i > 0 && <div style={separatorStyle} />}
                {group}
              </React.Fragment>
            ))
          ) : (
            <div style={{ fontSize: 11, color: pantryColors.gray[400], padding: '6px 10px' }}>
              All standard fields are shown
            </div>
          )}
          <div style={separatorStyle} />
          <MenuItem
            onSelect={() => select(onAddCustom)}
            disabled={customDisabled}
            title={customDisabled ? `Up to ${MAX_CUSTOM_SPECS} custom specifications` : undefined}
          >
            <PlusIcon />
            Custom specification
          </MenuItem>
        </div>,
        document.body,
      )
    : null

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => (open ? close() : setOpen(true))}
        style={specAddButtonStyle(disabled)}
      >
        <PlusIcon />
        {label}
      </button>
      {menu}
    </>
  )
}
